import { CommonModule, NgOptimizedImage } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import {
  getAddedIds,
  getAddedIdsString,
  getAllIds,
  getDeletedIds,
  getDeletedIdsString,
  getIds,
  getJoinedIdsString,
  getSectionNameString,
} from '@app/features/scope-overview/utils/scope-configuration.utils'
import { ScopeConfiguration, ScopeVersion, StatusType } from '@core/model/scope-version'
import { trackById, untilDestroyed } from '@shared/utils/utils'
import { cloneDeep, isNil, sortBy } from 'lodash'
import { Observable, combineLatest, take } from 'rxjs'

import { KeyValue } from '@app/core/types/key-value.type'
import { LibraryManagementNgrxModule } from '@app/features/library-management/library-management-ngrx.module'
import { LibraryDeliverableEntryDetailsService } from '@app/features/library-management/services/library-deliverable-entry-details.service'
import { LibraryDeliverableEntryDetails } from '@app/features/library-management/store/models/deliverable/library-deliverable-entry-details.model'
import { DeliverablePromptOption } from '@app/features/scope-overview/model/deliverable-prompt.model'
import { FeePromptOption } from '@app/features/scope-overview/model/fee-prompt.model'
import { ScenarioCategory } from '@app/features/scope-overview/model/scenario-category.model'
import { ScenarioQuestion } from '@app/features/scope-overview/model/scenario-question.model'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { ScopeConfigurationActions } from '@app/features/scope-overview/store/actions/scope-configuration.actions'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { ScopeConfigurationSelectors } from '@app/features/scope-overview/store/selectors/scope-configuration.selectors'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { getConfigurationWithEmptyAnswerValues } from '@app/features/scope-overview/utils/get-configuation-with-empty-answer-values.util'
import { getQuestionValueByType } from '@app/features/scope-overview/utils/get-question-value-by-type.util'
import { QuestionType } from '@core/model/enums/question-type.enum'
import { Store } from '@ngrx/store'
import { FormulaBuilderComponent } from '@shared/components/formula-builder/formula-builder.component'
import { ScopeUiCheckboxComponent } from '@shared/components/ui-components/scope-ui-checkbox/scope-ui-checkbox.component'
import { ScopeUiDatepickerComponent } from '@shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.component'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { FormulaService, ScenarioDependencyGraph } from '@shared/services/formula.service'
import { SharedModule } from '@shared/shared.module'
import { SNACKBAR_LENGTH_SHORT, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'
import { DetailedCellError } from 'hyperformula'
import { filter } from 'rxjs/operators'

interface ConfigurationState {
  enabled?: boolean
  completed?: boolean
  updated?: boolean
  excluded?: boolean
  displayConditionMet?: boolean
  hasPrompts?: boolean
  errorCount?: number
  control?: FormControl
  feeControl?: FormControl
  calcError?: boolean
  errorMessage?: string
  deliverablePromptOptions?: DeliverablePromptOption[]
  feePromptOptions?: FeePromptOption[]
}

@Component({
  selector: 'scope-configuration',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ScopeUiInputComponent,
    FormulaBuilderComponent,
    ScopeUiDatepickerComponent,
    ScopeUiDropdownComponent,
    NgOptimizedImage,
    LibraryManagementNgrxModule,
    ScopeUiCheckboxComponent,
  ],
  templateUrl: './scope-configuration.component.html',
  styleUrls: ['./scope-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScopeConfigurationComponent implements OnInit, OnDestroy {
  @Input() set currentScope(scopeVersion: ScopeVersion) {
    if (scopeVersion && this.scopeVersion?.identity?.scenario?.id !== scopeVersion.identity.scenario.id)
      this.store.dispatch(ScopeOverviewActions.getScenario({ scenarioId: scopeVersion.identity.scenario.id }))

    this.scopeVersion = scopeVersion

    if (scopeVersion) {
      if (!this.changeRequestId) {
        this.editable = this.approvalFlowService.isScopeEditable(scopeVersion)
      } else if (!this.scenarioInitialized) this.initializeScenario()

      this.configurationSubmitted =
        scopeVersion.configurationSubmitted || scopeVersion.configurationSubmitted === undefined
    }
  }

  scenario$: Observable<Scenario> = this.store.select(ScopeOverviewSelectors.selectScenario)
  saveLoading$: Observable<boolean> = this.store.select(ScopeConfigurationSelectors.selectLoadingSaveConfiguration)
  submitLoading$: Observable<boolean> = this.store.select(ScopeConfigurationSelectors.selectLoadingSubmitConfiguration)
  loadingModifiers$: Observable<boolean> = this.store.select(ScopeConfigurationSelectors.selectLoadingModifiers)
  configuration$: Observable<ScopeConfiguration> = this.store.select(ScopeConfigurationSelectors.selectConfiguration)

  scopeVersion: ScopeVersion
  scenario: Scenario
  scenarioInitialized: boolean
  configuration: ScopeConfiguration
  selectedCategoryId: number
  changeRequestId?: number

  private _editable = false
  get editable(): boolean {
    return this._editable
  }
  set editable(value: boolean) {
    this._editable = value
  }

  private _configurationSubmitted = false
  get configurationSubmitted(): boolean {
    return this._configurationSubmitted
  }
  set configurationSubmitted(value: boolean) {
    this._configurationSubmitted = value
  }
  formSubmitted: boolean
  totalErrorCount: number
  includedCategories: number[]
  feePlaceHolder = 'Fee value %'
  isDropdownOpen = new Map<number, boolean>()
  isFeeDropdownOpen = new Map<number, boolean>()
  editDeliverablePromptAgree: boolean
  editChangeRequestByScenarioAgree: boolean

  deliverableMap: KeyValue<LibraryDeliverableEntryDetails> = {}
  showFormulaMap: KeyValue<boolean> = {}
  categoryStates: KeyValue<ConfigurationState> = {}
  questionStates: KeyValue<ConfigurationState> = {}
  isApplyingConfiguration = false
  private pendingConfigurationActions: (() => void)[] = []
  private configurationProcessingTimeoutId: ReturnType<typeof setTimeout> | null = null
  private isDestroyed = false
  private dependencyGraph: ScenarioDependencyGraph = {
    formulaDependentsByField: {},
    questionDisplayDependentsByField: {},
    categoryDisplayDependentsByField: {},
  }
  private questionsById = new Map<number, ScenarioQuestion>()
  private categoriesById = new Map<number, ScenarioCategory>()
  private questionOrder = new Map<number, number>()
  private questionIdsInOrder: number[] = []

  constructor(
    protected store: Store,
    protected cdr: ChangeDetectorRef,
    protected formulaService: FormulaService,
    protected approvalFlowService: ApprovalFlowService,
    protected libraryDeliverableEntryDetailsService: LibraryDeliverableEntryDetailsService,
    protected snackbarService: SnackbarService
  ) {
    this.destroy$ = untilDestroyed()
  }

  ngOnInit() {
    this.getFormData()
  }

  getFormData() {
    combineLatest([this.scenario$, this.configuration$])
      .pipe(
        this.destroy$(),
        filter(([scenario, _]) => !!scenario),
        take(1)
      )
      .subscribe(([scenario, configuration]: [Scenario, ScopeConfiguration]) => {
        this.scenario = scenario
        this.configuration = { ...(configuration || {}) }
        if (!this.scenarioInitialized) this.initializeScenario()
      })
  }

  initializeScenario() {
    if (this.scopeVersion && this.scenario) {
      const dynamicFieldValues = Object.entries(this.scopeVersion?.dynamicFields || {}).reduce(
        (acc: any[], [key, value]) => {
          acc.push({ name: key, value: value })
          return acc
        },
        []
      )

      this.formulaService.initialise(this.scenario, dynamicFieldValues)
      this.initializeDependencyState()
      let enableNextCategory = true

      if (this.scenario.categories?.length) {
        let initialCategoryId: number
        this.scenario.categories.forEach((category) => {
          const completed = this.isCategoryCompleted(category)
          const excluded = this.evaluateCategoryExcluded(category)
          const hasPrompts = this.isCategoryHasPrompts(category)

          this.categoryStates[category.id] = { enabled: enableNextCategory, completed, excluded, hasPrompts }

          if (!completed && !excluded && !initialCategoryId) {
            initialCategoryId = category.id
            enableNextCategory = false
          }
        })
        this.refreshAllDependencyStates()
        this.setIncludedCategories()
        initialCategoryId = initialCategoryId || this.getInitialCategoryId()
        this.selectCategory(initialCategoryId)
        this.getAllDeliverablePromptOptions()
      }
      this.scenarioInitialized = true
      this.cdr.detectChanges()
    }
  }

  private initializeDependencyState() {
    this.dependencyGraph = this.formulaService.buildScenarioDependencyGraph(this.scenario)
    this.questionsById.clear()
    this.categoriesById.clear()
    this.questionOrder.clear()
    this.questionIdsInOrder = []

    let order = 0
    this.scenario.categories.forEach((category) => {
      this.categoriesById.set(category.id, category)
      category.questions.forEach((question) => {
        this.questionsById.set(question.id, question)
        this.questionOrder.set(question.id, order++)
        this.questionIdsInOrder.push(question.id)
      })
    })

    this.sortDependencyGraphQuestionDependents()
  }

  private refreshAllDependencyStates() {
    this.scenario.categories.forEach((category) => this.refreshCategoryDependencies(category))
  }

  private refreshCategoryDependencies(category: ScenarioCategory) {
    if (!this.categoryStates[category.id]) this.categoryStates[category.id] = {}

    this.updateCategoryExcludedState(category)
    this.categoryStates[category.id].hasPrompts = this.isCategoryHasPrompts(category)

    category.questions.forEach((question) => this.updateQuestionExclusionState(category, question))
  }

  private evaluateCategoryExcluded(category: ScenarioCategory): boolean {
    return !!(
      category.displayCondition && this.formulaService.calculate(category.displayCondition, 'categoryDisplay') === false
    )
  }

  private updateCategoryExcludedState(category: ScenarioCategory): boolean {
    const previousExcluded = this.categoryStates[category.id]?.excluded
    const nextExcluded = this.evaluateCategoryExcluded(category)
    this.categoryStates[category.id].excluded = nextExcluded
    return previousExcluded !== nextExcluded
  }

  private evaluateQuestionExcluded(question: ScenarioQuestion): boolean {
    return !!(
      question.displayCondition && this.formulaService.calculate(question.displayCondition, 'questionDisplay') === false
    )
  }

  private updateQuestionExclusionState(category: ScenarioCategory, question: ScenarioQuestion) {
    const questionWasExcluded = this.questionStates[question.id]?.excluded
    const excluded = this.evaluateQuestionExcluded(question)
    const displayConditionMet = !excluded

    this.questionStates[question.id] = {
      ...this.questionStates[question.id],
      excluded,
      displayConditionMet,
    }

    if (
      excluded &&
      !questionWasExcluded &&
      question.value &&
      question.type !== QuestionType.DELIVERABLE &&
      question.type !== QuestionType.FEE
    ) {
      this.questionStates[question.id].control?.setValue(null)
      this.setAnswer(question, null, true, true)
      return
    }

    this.setupQuestion(question)
  }

  getAllDeliverablePromptOptions() {
    const deliverableIds = [
      ...new Set(
        this.scenario.categories
          .flatMap((c) => c.questions)
          .flatMap((q) =>
            q.deliverablePrompt ? q.deliverablePrompt.deliverablePromptOptions?.map((o) => o.deliverableId) : []
          )
      ),
    ]

    if (deliverableIds.length)
      this.libraryDeliverableEntryDetailsService
        .getDeliverableEntryDetailsByIds(deliverableIds)
        .subscribe((deliverables: LibraryDeliverableEntryDetails[]) => {
          deliverables.forEach((d) => (this.deliverableMap[d.libraryDeliverableEntryId] = d))
          this.cdr.detectChanges()
        })
  }

  setIncludedCategories() {
    this.includedCategories = this.scenario.categories
      .filter((c) => !this.categoryStates[c.id].excluded || this.categoryStates[c.id].hasPrompts)
      .map((c) => c.id)
  }

  selectCategory(categoryId: number) {
    if (this.editable && !this.categoryStates[categoryId]?.enabled) return

    this.formSubmitted = false
    const category = this.scenario.categories.find((c) => c.id === categoryId)

    category.questions.forEach((q) => {
      this.questionStates[q.id] = { ...this.questionStates[q.id] }
      if (this.isQuestionShown(q) || q.hidden) this.setupQuestion(q)
    })

    if (!this.categoryStates[categoryId]) this.categoryStates[categoryId] = {}
    if (!this.categoryStates[categoryId]?.completed) this.setCategoryCompleted(category)

    if (isNil(this.categoryStates[categoryId]?.updated))
      this.categoryStates[categoryId].updated =
        !this.categoryStates[categoryId]?.completed || this.categoryStates[categoryId]?.errorCount > 0

    if (this.categoryStates[categoryId]?.errorCount) this.formSubmitted = true

    this.selectedCategoryId = categoryId
    this.cdr.detectChanges()
  }

  private setupQuestion(question: ScenarioQuestion) {
    if (question.defaultValue && !question.value && question.type !== QuestionType.FEE)
      question.value = question.defaultValue

    if (question.value === undefined) question.value = null

    switch (question.type) {
      case QuestionType.DELIVERABLE:
        if (question.deliverablePrompt.deliverablePromptOptions?.length) this.setupDeliverablePrompt(question)
        break
      case QuestionType.FEE:
        this.setupFeePrompt(question)
        break
      case QuestionType.FORMULA:
        this.calculateFormulaAnswer(question)
        break
      case QuestionType.DATE:
        if (this.questionStates[question.id].control) {
          this.questionStates[question.id].control.setValue(question.value)
        } else {
          this.questionStates[question.id].control = new FormControl(
            question.value,
            question.mandatory ? this.dateRequiredValidator() : null
          )
        }
        break
      default:
        if (this.questionStates[question.id].control) {
          this.questionStates[question.id].control.setValue(question.value)
        } else {
          this.questionStates[question.id].control = new FormControl(
            question.value,
            question.mandatory ? Validators.required : null
          )
        }
        this.updateQuestionConfigurationValue(question)
    }
  }

  private setupPromptQuestion(question: ScenarioQuestion, selectedIdsArray: number[]) {
    this.questionStates[question.id].control = new FormControl(
      selectedIdsArray,
      question.mandatory ? [this.promptRequiredValidator()] : []
    )
    const allIds = getAllIds(question.value)
    if ((!allIds?.length && selectedIdsArray?.length) || allIds?.length !== selectedIdsArray?.length) {
      const selectedIdsString = this.getSelectedIdsString(question, selectedIdsArray)
      this.setAnswer(question, selectedIdsString, true)
      this.categoryStates[question.categoryId].updated = true
    }

    this.cdr.detectChanges()
  }

  private setupFeePromptQuestion(question: ScenarioQuestion, selectedIdsArray: number[]) {
    this.questionStates[question.id].feeControl = new FormControl(
      selectedIdsArray,
      question.mandatory ? [this.promptRequiredValidator()] : []
    )

    if (!question.value && (selectedIdsArray.length || selectedIdsArray.length)) {
      let sectionName = question?.deliverablePrompt?.scopeSectionName
      sectionName = sectionName ? ',sectionName=' + sectionName : ''
      let idList = selectedIdsArray.join(',')
      if (selectedIdsArray) idList = selectedIdsArray.join(',') + '|' + idList
      this.setAnswer(question, idList + sectionName, true)
      this.categoryStates[question.categoryId].updated = true
    }

    this.cdr.detectChanges()
  }

  private updateSelectable(question: ScenarioQuestion) {
    const selectedFeeIds = this.questionStates[question.id].feePromptOptions
      ? this.questionStates[question.id].feePromptOptions.filter((o) => o.selected).map((o) => o.feeId)
      : [question.feeValue]

    const selectedIdsString = this.getSelectedIdsString(
      question,
      this.getSelectedIds(this.questionStates[question.id].deliverablePromptOptions, 'deliverableId')
    )
    const selectedFeeIdsString = selectedFeeIds.length > 0 ? selectedFeeIds.join(',') : null
    let finalValueString = ''

    if (this.questionStates[question.id].feeControl) {
      finalValueString = [selectedFeeIdsString, selectedIdsString].join(
        selectedFeeIdsString && selectedIdsString ? '|' : ''
      )
      this.questionStates[question.id].feeControl.setValue(selectedFeeIdsString)
    }
    if (this.questionStates[question.id].control) {
      finalValueString = [finalValueString, selectedIdsString].join(finalValueString && selectedIdsString ? '|' : '')
      this.questionStates[question.id].control.setValue(selectedIdsString)
    }

    if (question.feePrompt) {
      question.feeValue = selectedFeeIdsString
      this.configuration = {
        ...this.configuration,
        [question.categoryId]: {
          ...this.configuration?.[question.categoryId],
          [question.id]: finalValueString || null,
        },
      }
    }

    this.setAnswer(question, finalValueString)
  }

  private getSelectedIdsString(question: ScenarioQuestion, selectedIdsArray: number[]) {
    const sectionName = getSectionNameString(question)

    const addedIds = []
    const deletedIds = []

    this.getDeliverablePromptOptions(question)?.forEach((option) => {
      if (option.added) addedIds.push(option.deliverableId)
      if (option.deleted) deletedIds.push(option.deliverableId)
    })

    const selectedIdsString = getJoinedIdsString(selectedIdsArray)
    const deletedIdsString = getDeletedIdsString(deletedIds)
    const addedIdsString = getAddedIdsString(addedIds)

    return `${selectedIdsString}${sectionName},${deletedIdsString},${addedIdsString}`
  }

  calculateFormulaAnswer(question: ScenarioQuestion, shouldPropagateDependencies = true) {
    const result = this.formulaService.calculate(question.formula, 'formulaAnswer')

    const isError = result instanceof DetailedCellError
    const value = isError ? null : result

    this.questionStates[question.id] = isError ? {} : this.questionStates[question.id] || {}
    this.questionStates[question.id].calcError = isError
    this.questionStates[question.id].errorMessage = isError ? (result as DetailedCellError).message : null

    this.setAnswer(question, value, !isError, !shouldPropagateDependencies)
  }

  setCategoryCompleted(category: ScenarioCategory) {
    this.categoryStates[category.id].completed = !category.questions.find((q) => this.isMandatoryQuestionError(q))
  }

  setAnswer(question: ScenarioQuestion, value: any, autoUpdate = false, skipDependencyCheck = false) {
    if (!autoUpdate) {
      this.runWithConfigurationProcessing(() => this.applyAnswer(question, value, autoUpdate, skipDependencyCheck))
      return
    }

    this.applyAnswer(question, value, autoUpdate, skipDependencyCheck)
  }

  private applyAnswer(question: ScenarioQuestion, value: any, autoUpdate = false, skipDependencyCheck = false) {
    const perfLabel = `Configuration change: ${question?.fieldLabel || question?.fieldId || question?.id || 'unknown'}`
    if (!autoUpdate) this.formulaService.startPerfInteraction(perfLabel)

    try {
      if (!question || (question.type !== QuestionType.FEE && question.value == value)) return

      const valueChanged = this.setQuestionValue(question, value)
      if (!valueChanged) return

      if (!autoUpdate) this.updateConfiguration(this.configuration)

      this.formulaService.updateField(question)
      if (!skipDependencyCheck && question.fieldId) this.checkDependencies(question)
      this.setIncludedCategories()

      if (!autoUpdate) {
        if (!this.categoryStates[question.categoryId]) this.categoryStates[question.categoryId] = {}
        this.categoryStates[question.categoryId].updated = true
        this.totalErrorCount = Object.values(this.categoryStates).filter((c) => c.errorCount).length
        this.updateScenario(this.scenario)
        this.cdr.detectChanges()
      }
    } finally {
      if (!autoUpdate) this.formulaService.endPerfInteraction(perfLabel)
    }
  }

  setQuestionValue(question: ScenarioQuestion, value: any): boolean {
    const questionValue = this.questionStates[question.id].calcError
      ? null
      : getQuestionValueByType(question.type, value)
    const isDefaultValue = isNil(questionValue) && questionValue !== 0 && question.defaultValue

    const finalValue = isDefaultValue ? question.defaultValue : questionValue
    if (question.value !== finalValue) {
      question.value = isDefaultValue ? question.defaultValue : questionValue

      if (
        question.type === QuestionType.TEXT ||
        question.type === QuestionType.NUMBER ||
        question.type === QuestionType.DROPDOWN
      ) {
        if (!this.questionStates[question.id].control) {
          this.questionStates[question.id].control = new FormControl(
            question.value,
            question.mandatory ? Validators.required : null
          )
        } else {
          this.questionStates[question.id].control.setValue(question.value)
        }
      }

      this.updateQuestionConfigurationValue(question)
      return true
    }

    return false
  }

  setFeeAnswer(question: ScenarioQuestion, $event: any, autoUpdate = false) {
    if (!autoUpdate) {
      this.runWithConfigurationProcessing(() => this.applyFeeAnswer(question, $event, autoUpdate))
      return
    }

    this.applyFeeAnswer(question, $event, autoUpdate)
  }

  private applyFeeAnswer(question: ScenarioQuestion, $event: any, autoUpdate = false) {
    const perfLabel = `Configuration fee change: ${question?.fieldLabel || question?.fieldId || question?.id || 'unknown'}`
    if (!autoUpdate) this.formulaService.startPerfInteraction(perfLabel)

    try {
      if (question.feeValue == $event) return

      question.feeValue = getQuestionValueByType(question.type, $event)

      let finalValueString = question.feeValue
      if (this.questionStates[question.id].control) {
        const selectedIdsString = this.getSelectedIdsString(
          question,
          this.getSelectedIds(this.questionStates[question.id].deliverablePromptOptions, 'deliverableId')
        )
        finalValueString = [finalValueString, selectedIdsString].join(finalValueString && selectedIdsString ? '|' : '')
        this.questionStates[question.id].control.setValue(selectedIdsString)
      }

      this.configuration = {
        ...this.configuration,
        [question.categoryId]: { ...this.configuration?.[question.categoryId], [question.id]: finalValueString },
      }
      this.formulaService.updateFeeField(question)
      if (question.fieldId) this.checkDependencies(question)
      this.setIncludedCategories()

      if (!autoUpdate) {
        this.categoryStates[question.categoryId].updated = true
        this.totalErrorCount = Object.values(this.categoryStates).filter((c) => c.errorCount).length
        this.updateScenario(this.scenario)
        this.cdr.detectChanges()
      }
    } finally {
      if (!autoUpdate) this.formulaService.endPerfInteraction(perfLabel)
    }
  }

  private runWithConfigurationProcessing(action: () => void) {
    if (this.isDestroyed) {
      return
    }

    this.pendingConfigurationActions.push(action)

    if (this.isApplyingConfiguration) {
      return
    }

    this.isApplyingConfiguration = true
    this.snackbarService.showSnackbar('Applying changes...', SNACKBAR_LENGTH_SHORT, SnackbarEventType.INFO)

    this.configurationProcessingTimeoutId = setTimeout(() => {
      this.configurationProcessingTimeoutId = null
      if (this.isDestroyed) return

      try {
        while (this.pendingConfigurationActions.length && !this.isDestroyed) {
          const nextAction = this.pendingConfigurationActions.shift()
          nextAction?.()
        }
      } finally {
        this.isApplyingConfiguration = false
      }
    }, 0)
  }

  updateScenario(scenario: Scenario) {
    this.store.dispatch(ScopeOverviewActions.updateScenario({ scenario: cloneDeep(scenario), hasUpdates: true }))
  }

  private checkDependencies(question: ScenarioQuestion) {
    if (!question?.fieldId) {
      this.refreshAllDependencyStates()
      this.refreshCategoryValidationStates(this.scenario.categories.map((category) => category.id))
      return
    }

    this.runDependencyPropagation(question.fieldId.toLowerCase(), question.categoryId)
  }

  private runDependencyPropagation(initialFieldId: string, sourceCategoryId: number) {
    const queue: string[] = []
    const queuedFields = new Set<string>()
    const processedFields = new Set<string>()
    const affectedCategoryIds = new Set<number>([sourceCategoryId])
    const maxIterations = 10000
    let iterations = 0

    const enqueueField = (fieldId?: string) => {
      if (!fieldId) return
      const normalizedFieldId = fieldId.toLowerCase()
      if (processedFields.has(normalizedFieldId)) return
      if (queuedFields.has(normalizedFieldId)) return
      queuedFields.add(normalizedFieldId)
      queue.push(normalizedFieldId)
    }

    enqueueField(initialFieldId)

    while (queue.length) {
      if (iterations++ > maxIterations) {
        this.refreshAllDependencyStates()
        this.refreshCategoryValidationStates(this.scenario.categories.map((category) => category.id))
        return
      }

      const currentWaveFields = [...queue]
      queue.length = 0
      currentWaveFields.forEach((fieldId) => {
        queuedFields.delete(fieldId)
        processedFields.add(fieldId)
      })
      const waveQuestionDisplayDependents = new Set<number>()
      const waveCategoryDisplayDependents = new Set<number>()
      const waveFormulaDependents = new Set<number>()

      currentWaveFields.forEach((fieldId) => {
        ;(this.dependencyGraph.questionDisplayDependentsByField[fieldId] || []).forEach((questionId) =>
          waveQuestionDisplayDependents.add(questionId)
        )
        ;(this.dependencyGraph.categoryDisplayDependentsByField[fieldId] || []).forEach((categoryId) =>
          waveCategoryDisplayDependents.add(categoryId)
        )
        ;(this.dependencyGraph.formulaDependentsByField[fieldId] || []).forEach((questionId) =>
          waveFormulaDependents.add(questionId)
        )
      })

      this.questionIdsInOrder.forEach((questionId) => {
        if (!waveQuestionDisplayDependents.has(questionId)) return

        const question = this.questionsById.get(questionId)
        if (!question) return

        const previousValue = question.value
        const category = this.categoriesById.get(question.categoryId)
        if (!category) return

        this.updateQuestionExclusionState(category, question)
        affectedCategoryIds.add(question.categoryId)

        if (previousValue !== question.value) enqueueField(question.fieldId)
      })
      ;[...waveCategoryDisplayDependents].forEach((categoryId) => {
        const category = this.categoriesById.get(categoryId)
        if (!category) return

        if (!this.categoryStates[category.id]) this.categoryStates[category.id] = {}
        const categoryExcludedChanged = this.updateCategoryExcludedState(category)
        if (!categoryExcludedChanged) {
          return
        }

        const previousValues = new Map<number, any>()
        category.questions.forEach((question) => previousValues.set(question.id, question.value))
        category.questions.forEach((question) => this.updateQuestionExclusionState(category, question))
        affectedCategoryIds.add(category.id)

        category.questions.forEach((question) => {
          if (previousValues.get(question.id) !== question.value) enqueueField(question.fieldId)
        })
      })

      this.questionIdsInOrder.forEach((questionId) => {
        if (!waveFormulaDependents.has(questionId)) return

        const question = this.questionsById.get(questionId)
        if (!question || this.questionStates[question.id]?.excluded) return

        const previousValue = question.value
        this.calculateFormulaAnswer(question, false)
        affectedCategoryIds.add(question.categoryId)

        if (previousValue !== question.value) enqueueField(question.fieldId)
      })
    }

    this.refreshCategoryValidationStates(Array.from(affectedCategoryIds))
  }

  private sortQuestionIdsByOrder(questionIds: number[] = []): number[] {
    return [...questionIds].sort((questionIdA, questionIdB) => {
      const orderA = this.questionOrder.get(questionIdA) ?? Number.MAX_SAFE_INTEGER
      const orderB = this.questionOrder.get(questionIdB) ?? Number.MAX_SAFE_INTEGER
      return orderA - orderB
    })
  }

  private sortDependencyGraphQuestionDependents() {
    Object.keys(this.dependencyGraph.questionDisplayDependentsByField).forEach((fieldId) => {
      this.dependencyGraph.questionDisplayDependentsByField[fieldId] = this.sortQuestionIdsByOrder(
        this.dependencyGraph.questionDisplayDependentsByField[fieldId]
      )
    })

    Object.keys(this.dependencyGraph.formulaDependentsByField).forEach((fieldId) => {
      this.dependencyGraph.formulaDependentsByField[fieldId] = this.sortQuestionIdsByOrder(
        this.dependencyGraph.formulaDependentsByField[fieldId]
      )
    })
  }

  private refreshCategoryValidationStates(categoryIds: number[]) {
    categoryIds.forEach((categoryId) => {
      const category = this.categoriesById.get(categoryId)
      if (!category || !this.categoryStates[category.id]) return

      this.categoryStates[category.id].hasPrompts = this.isCategoryHasPrompts(category)

      if (this.categoryStates[category.id].completed) {
        this.categoryStates[category.id].errorCount = category.questions.filter(
          (q) =>
            this.isMandatoryQuestionError(q) ||
            (!this.questionStates[q.id]?.excluded && this.questionStates[q.id]?.calcError)
        ).length
        if (this.categoryStates[category.id].errorCount) this.formSubmitted = true
      } else if (category.id === this.selectedCategoryId) {
        this.setCategoryCompleted(category)
      }
    })
  }

  /* #region Deliverables */
  setupDeliverablePrompt(question: ScenarioQuestion) {
    let selectedIdsArray = question.value?.split(',') || []

    const selectedIds = getIds(selectedIdsArray)
    const addedIds = getAddedIds(selectedIdsArray)
    const deletedIds = getDeletedIds(selectedIdsArray)

    this.questionStates[question.id].deliverablePromptOptions =
      question.deliverablePrompt.deliverablePromptOptions?.map((option) => ({
        ...option,
        selected: selectedIds.includes(option.deliverableId),
        added: addedIds?.includes(option.deliverableId),
        deleted: deletedIds?.includes(option.deliverableId),
      }))

    selectedIdsArray = this.getDeliverablePromptOptions(question)?.map((option) => option.deliverableId)
    this.setupPromptQuestion(question, selectedIdsArray)
  }

  selectDeliverable(deliverable: DeliverablePromptOption, question: ScenarioQuestion) {
    this.toggleDeliverableSelection(true, deliverable, question)
  }

  unselectDeliverable(deliverable: DeliverablePromptOption, question: ScenarioQuestion) {
    this.toggleDeliverableSelection(false, deliverable, question)
  }

  toggleDeliverableSelection(selected: boolean, deliverable: DeliverablePromptOption, question: ScenarioQuestion) {
    if (deliverable.required) return

    deliverable.selected = selected

    if (selected) {
      if (deliverable.deleted) deliverable.deleted = false
      else if (!deliverable.added) deliverable.added = true
    } else {
      if (deliverable.added) deliverable.added = false
      else if (!deliverable.deleted) deliverable.deleted = true
    }

    this.updateSelectable(question)

    if (!this.isDeliverableToggleAvailable(question)) this.toggleDropdown(question.id, true)
  }
  /* #endregion */

  /* #region Fees */
  setupFeePrompt(question: ScenarioQuestion) {
    const rawValue = this.configuration?.[question.categoryId]?.[question.id] ?? question.value
    if (rawValue?.includes('|')) {
      const feeValueParts = rawValue.split('|')
      question.feeValue = feeValueParts[0]
      question.value = feeValueParts[1]
    } else {
      question.feeValue = rawValue
      question.value = null
    }

    let selectedFeeIdsArray = question.feeValue?.split(',') || []
    let selectedIdsArray = question.value?.split(',') || []

    const deliverablePromptOption = question.deliverablePrompt?.deliverablePromptOptions.map((option) => {
      return { ...option, selected: option.required || selectedIdsArray.includes(option.deliverableId.toString()) }
    })
    this.questionStates[question.id].deliverablePromptOptions = deliverablePromptOption

    if (question.feePrompt) {
      const promptOptions = question.feePrompt.feePromptOptions.map((option) => {
        return { ...option, selected: option.required || selectedFeeIdsArray.includes(option.feeId.toString()) }
      })
      this.questionStates[question.id].feePromptOptions = promptOptions
      selectedFeeIdsArray = promptOptions?.filter((o) => o.selected).map((o) => o.feeId)
      selectedIdsArray = deliverablePromptOption?.filter((o) => o.selected).map((o) => o.deliverableId)
      this.setupFeePromptQuestion(question, selectedFeeIdsArray)
    } else {
      if (this.questionStates[question.id].control) {
        this.questionStates[question.id].control.setValue(question.value)
      } else {
        this.questionStates[question.id].control = new FormControl(
          question.value,
          question.mandatory ? Validators.required : null
        )
      }
      if (this.questionStates[question.id].feeControl) {
        this.questionStates[question.id].feeControl.setValue(question.feeValue)
      } else {
        this.questionStates[question.id].feeControl = new FormControl(
          question.feeValue,
          question.mandatory ? Validators.required : null
        )
      }
    }
  }

  selectFee(fee: any, question: ScenarioQuestion): void {
    fee.selected = true
    this.updateSelectable(question)
    if (!this.isFeeToggleAvailable(question)) this.toggleFeeDropdown(question.id)
  }

  unselectFee(fee: any, question: ScenarioQuestion): void {
    if (fee.required) return
    fee.selected = false
    this.updateSelectable(question)
  }
  /* #endregion */

  toggleDropdown(questionId: number, close?: boolean): void {
    const shouldOpen = !close && !this.isDropdownOpen.get(questionId)
    this.closeAllDropdowns()
    if (shouldOpen) this.isDropdownOpen.set(questionId, true)
    this.cdr.detectChanges()
  }

  toggleFeeDropdown(questionId: number): void {
    const shouldOpen = !this.isFeeDropdownOpen.get(questionId)
    this.closeAllDropdowns()
    if (shouldOpen) this.isFeeDropdownOpen.set(questionId, true)
    this.cdr.detectChanges()
  }

  private closeAllDropdowns(): void {
    this.isDropdownOpen.clear()
    this.isFeeDropdownOpen.clear()
  }

  save(category: ScenarioCategory, selectNext?: boolean) {
    this.prepareSaveConfiguration(category, (nextCategoryId) => {
      this.store.dispatch(
        ScopeConfigurationActions.saveConfiguration({
          scopeId: this.scopeVersion.identity.id,
          configuration: this.configuration,
        })
      )

      this.saveLoading$
        .pipe(
          filter((loading) => !loading),
          take(1)
        )
        .subscribe(() => this.updateStatesAfterSaveConfiguration(category.id, nextCategoryId, selectNext))
    })
  }

  updateStatesAfterSaveConfiguration(categoryId: number, nextCategoryId: number, selectNext?: boolean) {
    if (nextCategoryId) this.categoryStates[nextCategoryId].enabled = true
    this.categoryStates[categoryId].updated = false
    if (selectNext) this.selectCategory(nextCategoryId)
    this.editDeliverablePromptAgree = false
    this.editChangeRequestByScenarioAgree = false
  }

  prepareSaveConfiguration(category: ScenarioCategory, callback: (nextCategoryId: number) => void) {
    this.formSubmitted = true

    this.configuration = getConfigurationWithEmptyAnswerValues(category, this.configuration)
    this.updateConfiguration(this.configuration)

    if (this.isCategoryValid(category)) {
      const nextCategoryId = this.getNextCategoryId(category.id)
      callback(nextCategoryId)
    }
  }

  submitConfiguration() {
    this.store.dispatch(
      ScopeConfigurationActions.submitForm({
        scenarioId: this.scopeVersion.identity.id,
        configuration: this.configuration,
      })
    )
  }

  updateQuestionConfigurationValue(question: ScenarioQuestion) {
    this.configuration = {
      ...this.configuration,
      [question.categoryId]: { ...this.configuration?.[question.categoryId], [question.id]: question.value },
    }
  }

  updateConfiguration(configuration: ScopeConfiguration) {
    this.store.dispatch(ScopeConfigurationActions.updateConfiguration({ configuration }))
  }

  ngOnDestroy() {
    this.isDestroyed = true

    if (this.configurationProcessingTimeoutId) {
      clearTimeout(this.configurationProcessingTimeoutId)
      this.configurationProcessingTimeoutId = null
    }

    this.pendingConfigurationActions.length = 0
    this.isApplyingConfiguration = false
    this.store.dispatch(ScopeOverviewActions.resetScenario())
  }

  /* #region Getters */
  getDisplayText = (question) =>
    question.type == QuestionType.FEE && question.fieldId
      ? `${question.displayText}: ${question.fieldId}`
      : question.displayText

  getInitialCategoryId = () =>
    this.scopeVersion.status === StatusType.CONFIG_DRAFT
      ? this.includedCategories?.[this.includedCategories?.length - 1]
      : this.includedCategories?.[0]

  getNextCategoryId = (categoryId: number) =>
    this.includedCategories?.[this.includedCategories?.indexOf(categoryId) + 1]

  getDeliverablePromptOptions = (question: ScenarioQuestion): DeliverablePromptOption[] =>
    sortBy(
      this.questionStates[question.id]?.deliverablePromptOptions?.filter(
        (option) =>
          (this.isDeliverableSelected(question, option.required) || option.selected || option.deleted) &&
          (!this.categoryStates[question.categoryId]?.excluded || this.categoryStates[question.categoryId]?.hasPrompts)
      ),
      (option) => !option.required
    )

  getDeliverableName = (deliverableEntryDetails: LibraryDeliverableEntryDetails): string =>
    `${deliverableEntryDetails.libraryItemName} (${deliverableEntryDetails.disciplineName})`

  getSelectedIds = (options, key: string): number[] => options.filter((o) => o.selected).map((o) => o[key])
  /* #endregion */

  /* #region Checks */
  isSubmitAvailable = (nextCategoryId: number) => !nextCategoryId && !this.configurationSubmitted
  isSubmitDisabled = (nextCategoryId: number) =>
    !this.editable ||
    (this.isEditDeliverablePromptAgreeRequired(nextCategoryId) && !this.editDeliverablePromptAgree) ||
    (this.isEditChangeRequestByScenarioAgreeRequired(nextCategoryId) && !this.editChangeRequestByScenarioAgree) ||
    !!this.scenario.categories.find((category) => this.categoryStates[category.id]?.errorCount)

  isSaveAvailable = (categoryId: number) => this.categoryStates[categoryId]?.updated
  isSaveDisabled = (categoryId: number) => !this.editable || !!this.categoryStates[categoryId]?.errorCount

  isEditDeliverablePromptAgreeRequired = (nextCategoryId: number) =>
    this.isSubmitAvailable(nextCategoryId) &&
    !!this.scenario.categories.find((category) =>
      category.questions.find(
        (question) =>
          this.isDeliverableExcluded(question) ||
          this.questionStates[question.id]?.deliverablePromptOptions?.find((option) => option.deleted)
      )
    )

  isEditChangeRequestByScenarioAgreeRequired = (nextCategoryId: number) =>
    this.isSubmitAvailable(nextCategoryId) && !!this.changeRequestId

  isQuestionShown = (question: ScenarioQuestion) => {
    const isExcluded = this.evaluateQuestionExcluded(question)
    const isDeliverableShown = isExcluded
      ? getAllIds(this.configuration?.[question.categoryId]?.[question.id])?.length
      : true

    return !question.hidden && (question.type === QuestionType.DELIVERABLE ? isDeliverableShown : !isExcluded)
  }

  isCategoryCompleted = (category: ScenarioCategory) =>
    !!this.configuration?.[category.id] && !category.questions.find((q) => this.isMandatoryQuestionError(q))

  isMandatoryQuestionError = (question: ScenarioQuestion) =>
    question.mandatory && this.isQuestionShown(question) && !question.value

  isCategoryExcluded = (category: ScenarioCategory) =>
    this.categoryStates[category.id]?.excluded && !this.categoryStates[category.id]?.hasPrompts

  showCategoryWarning = (category: ScenarioCategory) =>
    (this.categoryStates[category.id]?.excluded && this.categoryStates[category.id]?.hasPrompts) ||
    this.isDeliverableExcludedInCategory(category)

  isCategoryHasPrompts = (category: ScenarioCategory) =>
    this.categoryStates?.[category.id]?.enabled &&
    category.questions.some(
      (q) => q.type === QuestionType.DELIVERABLE && getAllIds(this.configuration[q.categoryId]?.[q.id])?.length
    )

  isDeliverableToggleAvailable = (question: ScenarioQuestion): boolean =>
    this.editable &&
    !this.isDeliverableExcluded(question) &&
    !!this.questionStates[question.id]?.deliverablePromptOptions?.find(
      (option) => !(option.selected || option.deleted || option.added)
    )

  isDeliverableExcludedInCategory = (category: ScenarioCategory): boolean =>
    category.questions.some((q) => this.isDeliverableExcluded(q))

  isDeliverableExcluded = (question: ScenarioQuestion): boolean =>
    question.type === QuestionType.DELIVERABLE &&
    getAllIds(this.configuration[question.categoryId]?.[question.id])?.length &&
    (this.questionStates[question.id]?.excluded || question.hidden)

  isDeliverableSelected = (question: ScenarioQuestion, required: boolean): boolean =>
    required && (this.questionStates[question.id]?.displayConditionMet ?? true)

  isFeeWithDeliverables = (question: ScenarioQuestion): boolean =>
    question.type === QuestionType.FEE &&
    question.deliverablePrompt &&
    this.questionStates[question.id].deliverablePromptOptions.length > 0

  isFeeToggleAvailable = (question: ScenarioQuestion): boolean =>
    this.editable && !!this.questionStates[question.id].feePromptOptions?.find((option) => !option.selected)

  isCategoryValid = (category: ScenarioCategory): boolean =>
    !category.questions.some((q) => {
      const control = !this.questionStates[q.id]?.excluded && this.questionStates[q.id]?.control
      if (control && !control.valid) {
        document.getElementById(q.id.toString()).scrollIntoView()
        return true
      }
      return false
    })
  /* #endregion */

  dateRequiredValidator =
    (): ValidatorFn =>
    (control: AbstractControl): ValidationErrors | null =>
      !control.value?.startDate ? { required: true } : null

  promptRequiredValidator =
    (): ValidatorFn =>
    (control: AbstractControl): ValidationErrors | null =>
      control.value?.length > 0 ? null : { required: true }

  trackByFeeId = (index: number, fee: any): string => fee.feeId
  trackByDeliverableId = (index: number, deliverable: any): string => deliverable.deliverableId

  protected readonly destroy$
  protected readonly QuestionType = QuestionType
  protected readonly trackById = trackById
  protected readonly StatusType = StatusType
}
